import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button, useToast } from "@/components/common";
import { contactsService } from "@/api/services/contacts.service";
import { messagesService, parseCreateThreadError, } from "@/api/services/messages.service";
import { useAuth } from "@/context/AuthContext";
import { getUserFullName } from "@/utils/formatDisplayName";
import styles from "./SelectContactModal.module.css";
const CONTACT_TYPE_OPTIONS = [
    { value: "client", label: "Client" },
    { value: "surveyor", label: "Surveyor" },
    { value: "contractor", label: "Contractor" },
    { value: "subcontractor", label: "Subcontractor" },
];
const SEARCH_DEBOUNCE_MS = 300;
const getContactDisplayName = (c) => {
    const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
    return name || c.companyName || c.contactId || "Unknown";
};
export const SelectContactModal = ({ isOpen, onClose, onSuccess, }) => {
    const { user } = useAuth();
    const { error: showError, info: showInfo } = useToast();
    const [contactType, setContactType] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [selectedContactName, setSelectedContactName] = useState("");
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const debounceRef = useRef(null);
    const contactDropdownRef = useRef(null);
    const fetchContacts = useCallback(async () => {
        if (!contactType) {
            setContacts([]);
            return;
        }
        setLoading(true);
        try {
            const response = await contactsService.getContacts({
                contactType,
                search: debouncedSearch || undefined,
                page: 1,
            });
            setContacts(response.data.data ?? []);
        }
        catch {
            setContacts([]);
        }
        finally {
            setLoading(false);
        }
    }, [contactType, debouncedSearch]);
    useEffect(() => {
        if (searchQuery === "") {
            setDebouncedSearch("");
            return;
        }
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            debounceRef.current = null;
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, [searchQuery]);
    useEffect(() => {
        if (isOpen && contactType) {
            fetchContacts();
        }
        else if (!contactType) {
            setContacts([]);
        }
    }, [isOpen, contactType, debouncedSearch, fetchContacts]);
    useEffect(() => {
        if (!isOpen) {
            setContactType("");
            setSearchQuery("");
            setDebouncedSearch("");
            setSelectedContactId(null);
            setSelectedContactName("");
            setShowContactDropdown(false);
            setMessage("");
        }
    }, [isOpen]);
    useEffect(() => {
        if (!showContactDropdown)
            return;
        const container = contactDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowContactDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowContactDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showContactDropdown]);
    const selectContact = useCallback((contact) => {
        const displayName = getContactDisplayName(contact);
        setSelectedContactId(contact.contactId);
        setSelectedContactName(displayName);
        setShowContactDropdown(false);
    }, []);
    const handleClearContact = useCallback(() => {
        setSelectedContactId(null);
        setSelectedContactName("");
        setSearchQuery("");
        setShowContactDropdown(false);
    }, []);
    const handleSend = async () => {
        if (!selectedContactId) {
            showError("Please select a contact.");
            return;
        }
        if (!message.trim()) {
            showError("Please enter a message.");
            return;
        }
        setSending(true);
        try {
            let threadResult = await messagesService.getThreadByGeneral(selectedContactId);
            if (!threadResult) {
                try {
                    const createRes = await messagesService.createThread({
                        contactId: selectedContactId,
                        type: "GENERAL",
                    });
                    const thread = createRes.data;
                    if (thread?.id) {
                        threadResult = {
                            thread,
                            messages: [],
                        };
                    }
                }
                catch (createErr) {
                    const { message: errMsg } = parseCreateThreadError(createErr);
                    showError(errMsg);
                    setSending(false);
                    return;
                }
            }
            if (threadResult?.thread?.id) {
                await messagesService.sendMessage({
                    threadId: threadResult.thread.id,
                    threadType: "general",
                    contactId: selectedContactId,
                    assignedcontactId: selectedContactId,
                    content: message.trim(),
                    senderId: user?.id,
                    senderName: getUserFullName(user, "Staff"),
                });
                showInfo("Message sent.");
                onSuccess?.(selectedContactId, contactType);
                onClose();
            }
        }
        catch {
            showError("Failed to send message.");
        }
        finally {
            setSending(false);
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: onClose, title: "Send message to contact", children: _jsxs("div", { className: styles.form, children: [_jsxs("div", { className: styles.field, children: [_jsx("label", { className: styles.label, children: "Contact Type" }), _jsxs("select", { className: styles.select, value: contactType, onChange: (e) => setContactType(e.target.value), children: [_jsx("option", { value: "", children: "Select contact type\u2026" }), CONTACT_TYPE_OPTIONS.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value)))] })] }), _jsxs("div", { className: styles.field, children: [_jsx("label", { className: styles.label, children: "Contact" }), _jsxs("div", { className: styles.searchableDropdown, ref: contactDropdownRef, children: [_jsxs("div", { className: styles.searchInputWrapper, children: [_jsx("input", { type: "text", className: styles.searchInput, placeholder: !contactType
                                                ? "Select contact type first"
                                                : "Type here to search", value: selectedContactId ? selectedContactName : searchQuery, onChange: (e) => {
                                                const value = e.target.value;
                                                setSearchQuery(value);
                                                if (selectedContactId) {
                                                    setSelectedContactId(null);
                                                    setSelectedContactName("");
                                                }
                                                if (!showContactDropdown)
                                                    setShowContactDropdown(true);
                                            }, onFocus: () => {
                                                if (contactType)
                                                    setShowContactDropdown(true);
                                            }, disabled: !contactType }), selectedContactId && (_jsx("button", { type: "button", className: styles.clearButton, onClick: handleClearContact, title: "Clear selection", children: "\u00D7" }))] }), showContactDropdown && contactType && (_jsx("div", { className: styles.dropdownList, children: loading ? (_jsx("div", { className: styles.dropdownEmpty, children: "Loading\u2026" })) : contacts.length === 0 ? (_jsx("div", { className: styles.dropdownEmpty, children: searchQuery.trim()
                                            ? "No contacts found"
                                            : "No contacts available" })) : (contacts.map((c) => (_jsxs("div", { className: styles.dropdownItem, onClick: () => selectContact(c), children: [_jsx("div", { className: styles.dropdownItemName, children: getContactDisplayName(c) }), c.email && (_jsx("div", { className: styles.dropdownItemEmail, children: c.email })), c.companyName && (_jsx("div", { className: styles.dropdownItemCompany, children: c.companyName }))] }, c.contactId)))) }))] })] }), _jsxs("div", { className: styles.field, children: [_jsx("label", { className: styles.label, children: "Message" }), _jsx("textarea", { className: styles.textarea, placeholder: "Type your message\u2026", value: message, onChange: (e) => setMessage(e.target.value), rows: 4 })] }), _jsxs("div", { className: styles.actions, children: [_jsx(Button, { variant: "secondary", onClick: onClose, disabled: sending, children: "Cancel" }), _jsx(Button, { variant: "primary", onClick: handleSend, loading: sending, disabled: sending ||
                                !selectedContactId ||
                                !message.trim(), children: "Send" })] })] }) }));
};
